
import React, { useEffect } from "react"
import { Row, Col, Card, CardBody, CardTitle, Table, Badge } from "reactstrap"
import { connect } from "react-redux"
import { getCompanyDashboardStats } from "../../../store/actions"
import Miniwidget from "../Miniwidget"
import ReactApexChart from "react-apexcharts"
import moment from "moment"
import SubscriptionWidget from "./SubscriptionWidget"

const CompanyDashboard = (props) => {
    const { stats, loading } = props

    useEffect(() => {
        props.getCompanyDashboardStats()
    }, [])

    if (loading || !stats) {
        return <div className="text-center p-5">Chargement des statistiques...</div>
    }

    const methodMap = {
        'cash': 'Espèces',
        'bank_transfer': 'Virement',
        'check': 'Chèque',
        'card': 'Carte',
        'other': 'Autre'
    }

    const reports = [
        { title: "Total Clients", iconClass: "account-group", total: stats.total_customers || "0", average: "", badgecolor: "primary" },
        { title: "Factures en attente", iconClass: "file-document-outline", total: stats.pending_invoices || "0", average: "", badgecolor: "warning" },
        { title: "Ventes du mois", iconClass: "cart-outline", total: `${(stats.monthly_sales || 0).toLocaleString()} MAD`, average: "", badgecolor: "success" },
        { title: "Balance TVA", iconClass: "calculator-variant", total: `${(stats.total_tva || 0).toLocaleString()} MAD`, average: "TVA", desc: "Collectée sur ventes", badgecolor: "info" },
        { title: "Dettes Fournisseurs", iconClass: "truck-delivery-outline", total: `${(stats.pending_supplier_bills || 0).toLocaleString()} MAD`, average: "", badgecolor: "danger" },
    ]

    const earningChartOptions = {
        chart: { toolbar: { show: false }, zoom: { enabled: false } },
        colors: ['#3c4ccf'],
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: 3 },
        xaxis: {
            categories: (stats.monthly_earnings || []).map(item => moment(item.month, "YYYY-MM").format("MMM")),
        },
        title: { text: 'Revenus (MAD)', align: 'left' }
    }

    const earningChartSeries = [{
        name: "Revenus",
        data: (stats.monthly_earnings || []).map(item => item.total)
    }]

    const customerChartOptions = {
        chart: { toolbar: { show: false } },
        plotOptions: { bar: { horizontal: false, columnWidth: '45%' } },
        dataLabels: { enabled: false },
        colors: ['#02a499'],
        xaxis: {
            categories: (stats.customer_growth || []).map(item => moment(item.month, "YYYY-MM").format("MMM")),
        },
    }

    const customerChartSeries = [{
        name: "Nouveaux Clients",
        data: (stats.customer_growth || []).map(item => item.count)
    }]

    return (
        <React.Fragment>
            <Miniwidget reports={reports} />

            <Row>
                <Col xl="8">
                    <Card>
                        <CardBody>
                            <CardTitle className="h4 mb-4">Derniers Paiements Recus</CardTitle>
                            {/* ... table content remains the same ... */}
                            <div className="table-responsive">
                                <Table className="table-centered table-nowrap mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Date</th>
                                            <th>Client</th>
                                            <th>Facture</th>
                                            <th>Montant</th>
                                            <th>Méthode</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(stats.recent_payments || []).map((payment, key) => (
                                            <tr key={key}>
                                                <td>{moment(payment.payment_date).format('DD/MM/YYYY')}</td>
                                                <td>{payment.customer?.company_name || payment.customer?.first_name}</td>
                                                <td>#{payment.invoice?.invoice_number}</td>
                                                <td>{payment.amount} MAD</td>
                                                <td>
                                                    <Badge color="info" pill>{methodMap[payment.method] || payment.method}</Badge>
                                                </td>
                                            </tr>
                                        ))}
                                        {(!stats.recent_payments || stats.recent_payments.length === 0) && (
                                            <tr>
                                                <td colSpan="5" className="text-center">Aucun paiement récent.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
                <Col xl="4">
                    <SubscriptionWidget />

                    <Card>
                        <CardBody>
                            <CardTitle className="h4 mb-4">Rappels & Échéances</CardTitle>
                            <ul className="list-group list-group-flush">
                                {(stats.upcoming_reminders || []).map((reminder, key) => (
                                    <li key={key} className="list-group-item px-0 py-3">
                                        <div className="d-flex">
                                            <div className="flex-shrink-0 me-3">
                                                <div className={`avatar-xs ${reminder.priority === 'high' ? 'text-danger' : 'text-primary'}`}>
                                                    <span className="avatar-title bg-light rounded-circle">
                                                        <i className={`mdi ${reminder.type === 'invoice' ? 'mdi-file-clock' : 'mdi-bell-outline'} font-size-18`}></i>
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="flex-grow-1">
                                                <h5 className="font-size-14 mb-1">{reminder.title}</h5>
                                                <p className="text-muted mb-0 font-size-13">{moment(reminder.reminder_date).format("DD/MM/YYYY")}</p>
                                            </div>
                                            {reminder.priority === 'high' && (
                                                <div className="flex-shrink-0">
                                                    <Badge color="danger" pill>Urgent</Badge>
                                                </div>
                                            )}
                                        </div>
                                    </li>
                                ))}
                                {(!stats.upcoming_reminders || stats.upcoming_reminders.length === 0) && (
                                    <li className="list-group-item px-0 text-center text-muted">Aucun rappel pour le moment.</li>
                                )}
                            </ul>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <Row>
                <Col xl="8">
                    <Card>
                        <CardBody>
                            <CardTitle className="h4 mb-4">Évolution des Revenus</CardTitle>
                            <ReactApexChart
                                options={earningChartOptions}
                                series={earningChartSeries}
                                type="line"
                                height="290"
                            />
                        </CardBody>
                    </Card>
                </Col>
                <Col xl="4">
                    <Card>
                        <CardBody>
                            <CardTitle className="h4 mb-4">Croissance Clients</CardTitle>
                            <ReactApexChart
                                options={customerChartOptions}
                                series={customerChartSeries}
                                type="bar"
                                height="290"
                            />
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </React.Fragment>
    )
}

const mapStateToProps = state => ({
    stats: state.DashboardCompany.stats,
    loading: state.DashboardCompany.loading
})

export default connect(mapStateToProps, { getCompanyDashboardStats })(CompanyDashboard)
